import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { authAPI } from '../../../services/apiService';
import MobileSignIn from './MobileSignIn';
import MobileSignUpStep1 from './MobileSignUpStep1';
import MobileSignUpStep2 from './MobileSignUpStep2';
import MobileVerify from './MobileVerify';
import MobileResetPasswordStep1 from './MobileResetPasswordStep1';
import MobileResetPasswordStep2 from './MobileResetPasswordStep2';
import MobileAuthLoading from './MobileAuthLoading';
import MobileAuthSuccess from './MobileAuthSuccess';

const MobileAuthFlow = ({ onAuthSuccess }) => {
  const { signIn, signUp } = useAuth();
  
  // Flow state management
  const [screen, setScreen] = useState('signin'); // signin, signupStep1, signupStep2, verify, resetStep1, resetStep2, loading, success
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  
  // Form data storage
  const [signUpData, setSignUpData] = useState({});
  const [email, setEmail] = useState('');
  const [userName, setUserName] = useState('');

  // Clear messages
  const clearMessages = () => {
    setError('');
    setMessage('');
  };

  // ============================================
  // SIGN IN HANDLERS
  // ============================================
  const handleSignIn = async (email, password) => {
    const logPrefix = '📱 MobileAuthFlow.handleSignIn';
    console.log(`${logPrefix}: Starting sign-in`, {
      email: email?.toLowerCase()?.trim(),
      timestamp: new Date().toISOString()
    });
    
    setLoading(true);
    clearMessages();

    try {
      console.log(`${logPrefix}: Calling signIn from AuthContext...`);
      const result = await signIn(email, password);
      console.log(`${logPrefix}: SignIn result received`, {
        success: result.success,
        hasError: !!result.error,
        error: result.error,
        hasUser: !!result.user,
        errorDetails: result.errorDetails
      });
      
      if (result.success) {
        // Extract user name if available
        const firstName = result.user?.firstName || result.user?.first_name || result.user?.given_name || '';
        setUserName(firstName);
        
        console.log(`${logPrefix}: ✅ Sign-in successful, showing success screen`);
        
        // Show success screen
        setScreen('success');
        setLoading(false);
        
        // Redirect after 1.5 seconds
        setTimeout(() => {
          onAuthSuccess();
        }, 1500);
      } else {
        console.warn(`${logPrefix}: ⚠️ Sign-in failed`, {
          error: result.error,
          errorDetails: result.errorDetails
        });
        
        // Use the error message from AuthContext (already user-friendly)
        setError(result.error || 'Sign in failed. Please try again.');
        setLoading(false);
      }
    } catch (err) {
      console.error(`${logPrefix}: ❌ Exception caught`, {
        errorType: err.constructor.name,
        errorName: err.name,
        errorMessage: err.message,
        errorStack: err.stack,
        response: err.response ? {
          status: err.response.status,
          statusText: err.response.statusText,
          data: err.response.data,
          headers: err.response.headers
        } : null,
        request: err.request ? {
          method: err.config?.method,
          url: err.config?.url,
          baseURL: err.config?.baseURL
        } : null
      });
      
      // Log full error as JSON for easier inspection
      console.error(`${logPrefix}: Full Error JSON`, JSON.stringify({
        name: err.name,
        message: err.message,
        stack: err.stack,
        response: err.response ? {
          status: err.response.status,
          data: err.response.data
        } : null
      }, null, 2));
      
      // Provide user-friendly error messages
      let errorMessage = 'An error occurred during sign in.';
      
      if (err.response) {
        const status = err.response.status;
        const detail = err.response.data?.detail || err.response.data?.message || err.response.data?.error;
        
        console.log(`${logPrefix}: Processing ${status} error`, { detail });
        
        if (status === 401) {
          errorMessage = detail || 'Invalid email or password. Please check your credentials and try again.';
        } else if (status === 403) {
          errorMessage = detail || 'Please verify your email address before signing in.';
        } else if (status === 404) {
          errorMessage = detail || 'No account found with this email address.';
        } else if (detail) {
          errorMessage = detail;
        } else {
          errorMessage = `Sign in failed (${status}). Please try again.`;
        }
      } else if (err.message) {
        if (err.message.includes('401')) {
          errorMessage = 'Invalid email or password. Please check your credentials and try again.';
        } else if (err.message.includes('Network Error') || err.message.includes('timeout')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else {
          errorMessage = err.message;
        }
      }
      
      setError(errorMessage);
      setLoading(false);
    }
  };

  // ============================================
  // SIGN UP HANDLERS
  // ============================================
  const handleSignUpStep1Next = (formData) => {
    setSignUpData(formData);
    setEmail(formData.email);
    setScreen('signupStep2');
  };

  const handleSignUpStep2Back = () => {
    setScreen('signupStep1');
  };

  const handleSignUp = async (completeFormData) => {
    setLoading(true);
    clearMessages();

    try {
      const result = await signUp(completeFormData);
      
      if (result.success) {
        setEmail(completeFormData.email);
        setUserName(completeFormData.firstName);
        setMessage('Account created! Please check your email for verification.');
        setScreen('verify');
      } else {
        setError(result.error || 'Signup failed. Please try again.');
      }
    } catch (err) {
      console.error('Signup error:', err);
      setError(err.response?.data?.detail || err.message || 'An unexpected error occurred during signup.');
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // EMAIL VERIFICATION HANDLERS
  // ============================================
  const handleVerifyEmail = async (email, code) => {
    setLoading(true);
    clearMessages();

    try {
      const response = await authAPI.verifyEmail(email, code);
      
      if (response.success) {
        setMessage('Email verified successfully! You can now sign in.');
        
        // Show success screen briefly
        setScreen('success');
        setTimeout(() => {
          setScreen('signin');
          setMessage('Email verified! Please sign in.');
        }, 1500);
      } else {
        setError(response.message || 'Verification failed. Please check your code.');
      }
    } catch (err) {
      console.error('Verification error:', err);
      setError(err.response?.data?.detail || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setLoading(true);
    clearMessages();

    try {
      const response = await authAPI.resendVerification(email);
      if (response.success) {
        setMessage('Verification code resent to your email.');
      } else {
        setError(response.message || 'Failed to resend verification code.');
      }
    } catch (err) {
      console.error('Resend verification error:', err);
      setError(err.response?.data?.detail || 'Failed to resend verification code.');
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // PASSWORD RESET HANDLERS
  // ============================================
  const handleSendResetCode = async (email) => {
    setLoading(true);
    clearMessages();

    try {
      await authAPI.forgotPassword(email);
      setEmail(email);
      setMessage('Reset code sent to your email.');
      setScreen('resetStep2');
    } catch (err) {
      console.error('Forgot password error:', err);
      setError(err.response?.data?.detail || 'Failed to send reset code.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (email, code, newPassword) => {
    setLoading(true);
    clearMessages();

    try {
      await authAPI.resetPassword(email, code, newPassword);
      setMessage('Password reset successfully! You can now sign in.');
      
      // Show success screen briefly
      setScreen('success');
      setTimeout(() => {
        setScreen('signin');
        setMessage('Password reset! Please sign in with your new password.');
      }, 1500);
    } catch (err) {
      console.error('Reset password error:', err);
      setError(err.response?.data?.detail || 'Password reset failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // NAVIGATION HANDLERS
  // ============================================
  const goToSignUp = () => {
    clearMessages();
    setScreen('signupStep1');
  };

  const goToSignIn = () => {
    clearMessages();
    setScreen('signin');
  };

  const goToForgotPassword = () => {
    clearMessages();
    setScreen('resetStep1');
  };

  // ============================================
  // RENDER CURRENT SCREEN
  // ============================================
  switch (screen) {
    case 'loading':
      return <MobileAuthLoading message="Loading..." />;

    case 'success':
      return <MobileAuthSuccess message="Success!" userName={userName} />;

    case 'signin':
      return (
        <MobileSignIn
          onSignIn={handleSignIn}
          onSwitchToSignUp={goToSignUp}
          onForgotPassword={goToForgotPassword}
          loading={loading}
          error={error}
        />
      );

    case 'signupStep1':
      return (
        <MobileSignUpStep1
          onNext={handleSignUpStep1Next}
          onBack={goToSignIn}
          initialData={signUpData}
          error={error}
        />
      );

    case 'signupStep2':
      return (
        <MobileSignUpStep2
          onSignUp={handleSignUp}
          onBack={handleSignUpStep2Back}
          formData={signUpData}
          loading={loading}
          error={error}
        />
      );

    case 'verify':
      return (
        <MobileVerify
          onVerify={handleVerifyEmail}
          onResend={handleResendVerification}
          email={email}
          loading={loading}
          error={error}
          message={message}
        />
      );

    case 'resetStep1':
      return (
        <MobileResetPasswordStep1
          onSendCode={handleSendResetCode}
          onBack={goToSignIn}
          loading={loading}
          error={error}
          message={message}
        />
      );

    case 'resetStep2':
      return (
        <MobileResetPasswordStep2
          onResetPassword={handleResetPassword}
          onBack={() => setScreen('resetStep1')}
          email={email}
          loading={loading}
          error={error}
        />
      );

    default:
      return <MobileSignIn onSignIn={handleSignIn} />;
  }
};

export default MobileAuthFlow;
